import React, { useState } from 'react';
import { Card, CardHeader, CardContent, CardTitle, CardDescription } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { 
    FileText, Play, Clock, X, CheckCircle, Send, 
    BarChart2, Plus, Trash2, ChevronRight, Activity,
    ClipboardIcon, LayoutDashboard, History
} from 'lucide-react';
import { Input } from '../../components/ui/Input';
import { useNavigate } from 'react-router-dom';
import { useData } from '../../contexts/DataContext';
import { useAuth } from '../../contexts/AuthContext';

export default function Assessments() {
    const { profiles, assessments, assignAssessment, adminEvaluations, patientQuestionnaires, addPatientQuestionnaire } = useData();
    const { user } = useAuth();
    const navigate = useNavigate();

    // Clinical Patient Modal
    const [isPatientModalOpen, setPatientModalOpen] = useState(false);
    const [selectedClinicalAssessment, setSelectedClinicalAssessment] = useState(null);
    const [isAssignModalOpen, setAssignModalOpen] = useState(false);
    const [selectedPatientId, setSelectedPatientId] = useState('');
    const [selectedQuestionnaireId, setSelectedQuestionnaireId] = useState('');
    const [selectedFrequency, setSelectedFrequency] = useState('Monthly');
    const [showSuccess, setShowSuccess] = useState(false);

    // Create questionnaire modal state
    const [isCreateModalOpen, setCreateModalOpen] = useState(false);
    const [createTitle, setCreateTitle] = useState('');
    const [createDuration, setCreateDuration] = useState('');
    const [createQuestions, setCreateQuestions] = useState(['', '']);
    const [createSuccess, setCreateSuccess] = useState(false);
    const [lastCreatedId, setLastCreatedId] = useState(null);

    // Merge dynamic assessments with static history for demo
    const RECENT_HISTORY = [
        ...assessments.map(a => ({
            id: a.id,
            student: a.patientName,
            assessment: a.type,
            date: new Date(a.date).toLocaleDateString(),
            score: typeof a.score === 'number' ? `${Math.round((a.score / (a.questions?.length * 4 || 20)) * 100)}%` : 'Completed',
            submitterRole: a.submitterRole
        })).reverse(),
        { id: 'h1', student: 'Alex Johnson', assessment: 'Quarterly Progress Check', date: 'Oct 14, 2026', score: '78%', submitterRole: 'Patient' },
        { id: 'h2', student: 'Sophia Davis', assessment: 'Early Intervention Screener', date: 'Oct 12, 2026', score: 'Pending', submitterRole: 'Admin' },
    ];

    // Filter profiles for the current therapist if applicable
    const assignedProfiles = profiles.filter(p => !user || user.role === 'Admin' || p.assignedTherapistId === user.id);

    const handleStartClinical = (assessment) => {
        setSelectedClinicalAssessment(assessment);
        setPatientModalOpen(true);
    };

    const confirmClinicalStart = (patient) => {
        navigate('/app/assessments/take', { state: { assessment: selectedClinicalAssessment, patient } });
    };

    const openAssignModal = (preSelectId = '') => {
        setSelectedPatientId('');
        setSelectedQuestionnaireId(preSelectId);
        setShowSuccess(false);
        setAssignModalOpen(true);
    };

    const handleAssignToPortal = () => {
        if (!selectedPatientId || !selectedQuestionnaireId) return alert('Please select both patient and questionnaire.');
        const patient = profiles.find(p => p.id.toString() === selectedPatientId);
        const q = patientQuestionnaires.find(q => q.id === selectedQuestionnaireId);
        assignAssessment({
            patientId: patient.id,
            patientName: patient.name,
            assessmentTitle: q.title,
            domains: q.domains,
            duration: q.duration,
            questions: q.questions,
            frequency: selectedFrequency
        });
        setShowSuccess(true);
        setTimeout(() => { setShowSuccess(false); setAssignModalOpen(false); }, 2000);
    };

    const handleAddQuestion = () => setCreateQuestions(prev => [...prev, '']);
    const handleRemoveQuestion = (idx) => setCreateQuestions(prev => prev.filter((_, i) => i !== idx));
    const handleQuestionChange = (idx, val) => setCreateQuestions(prev => prev.map((q, i) => i === idx ? val : q));

    const handleCreateSubmit = () => {
        if (!createTitle.trim()) return alert('Please enter a questionnaire title.');
        const validQuestions = createQuestions.filter(q => q.trim());
        if (validQuestions.length < 1) return alert('Please add at least one question.');
        const newQ = addPatientQuestionnaire({
            title: createTitle.trim(),
            duration: createDuration.trim() || '~10 mins',
            questions: validQuestions,
        });
        setLastCreatedId(newQ.id);
        setCreateSuccess(true);
        setTimeout(() => {
            setCreateSuccess(false);
            setCreateModalOpen(false);
        }, 2000);
    };

    return (
        <div className="space-y-10">
            {/* Header Area */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                <div>
                    <h1 className="text-4xl font-black text-slate-900 font-outfit tracking-tight">Evaluations</h1>
                    <p className="text-slate-500 mt-2 text-lg font-light">Conduct clinical checks or dispense forms to patient portals.</p>
                </div>
                <div className="flex gap-3">
                     <Button variant="outline" onClick={() => setCreateModalOpen(true)} className="rounded-2xl h-14 px-6 border-slate-200 bg-white font-black text-xs uppercase tracking-widest text-slate-600 hover:bg-slate-50 transition-all">
                        <Plus className="mr-2 h-4 w-4" /> New Form Template
                    </Button>
                    <Button onClick={() => openAssignModal()} className="rounded-2xl h-14 px-8 shadow-premium font-black text-xs uppercase tracking-widest bg-brand-600 hover:bg-brand-700 text-white transition-all">
                        <Send className="mr-3 h-4 w-4" /> Dispatch Form
                    </Button>
                </div>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-12 gap-10">
                <div className="xl:col-span-8 space-y-12">
                    
                    {/* Clinical Evaluations (In-Clinic) */}
                    <div>
                        <div className="flex items-center gap-4 mb-8">
                             <div className="h-12 w-12 rounded-2xl bg-brand-50 flex items-center justify-center text-brand-600 border border-brand-100 shadow-sm">
                                <LayoutDashboard className="h-6 w-6" />
                             </div>
                             <div>
                                <h2 className="text-2xl font-black text-slate-900 font-outfit tracking-tight">Clinical Assessments by Therapist</h2>
                                <p className="text-slate-400 font-bold text-[10px] uppercase tracking-widest mt-0.5">Available: {adminEvaluations.length}</p>
                             </div>
                        </div>
                        <div className="grid sm:grid-cols-2 gap-8">
                            {adminEvaluations.map(item => (
                                <Card key={item.id} className="border-0 shadow-soft rounded-[2.5rem] bg-white border border-slate-50 hover:shadow-premium hover:-translate-y-1 transition-all group overflow-hidden">
                                    <div className="p-8 h-full flex flex-col">
                                        <div className="mb-6 flex justify-between items-start">
                                            <Badge className="bg-slate-50 text-slate-400 border-slate-100 px-3 py-1 text-[10px] font-black uppercase tracking-widest rounded-xl">{item.type}</Badge>
                                             <div className="h-10 w-10 rounded-xl bg-slate-50 flex items-center justify-center text-slate-200 group-hover:bg-brand-50 group-hover:text-brand-400 transition-all">
                                                <ClipboardIcon className="h-5 w-5" />
                                             </div>
                                        </div>
                                        <h3 className="text-xl font-black text-slate-900 group-hover:text-brand-600 transition-colors uppercase tracking-tight font-outfit mb-3">{item.title}</h3>
                                        <div className="flex items-center gap-4 text-[10px] font-black uppercase text-slate-400 tracking-widest mb-10">
                                            <span className="flex items-center gap-1.5"><Activity className="h-3.5 w-3.5" /> {item.questions?.length??1} Tests Available</span>
                                            <span className="flex items-center gap-1.5"><Activity className="h-3.5 w-3.5" /> {item.domains} Domains</span>
                                            <span className="flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" /> {item.duration}</span>
                                        </div>
                                        <Button 
                                            className="w-full h-14 rounded-2xl bg-slate-900 hover:bg-slate-800 text-white font-black text-xs uppercase tracking-[0.2em] shadow-lg transition-all"
                                            onClick={() => handleStartClinical(item)}
                                        >
                                            Initialize Test <Play className="ml-3 h-3 w-3 fill-current" />
                                        </Button>
                                    </div>
                                </Card>
                            ))}
                        </div>
                    </div>

                    {/* At-Home Questionnaires */}
                    <div>
                        <div className="flex items-center gap-4 mb-8">
                             <div className="h-12 w-12 rounded-2xl bg-accent-50 flex items-center justify-center text-accent-600 border border-accent-100 shadow-sm">
                                <Send className="h-6 w-6" />
                             </div>
                             <div>
                                <h2 className="text-2xl font-black text-slate-900 font-outfit tracking-tight">Dispensed Assessment Protocols for Patient</h2>
                                <p className="text-slate-400 font-bold text-[10px] uppercase tracking-widest mt-0.5">Dispensed to Patient Portals: Available {patientQuestionnaires.length}</p>
                             </div>
                        </div>
                        <div className="grid sm:grid-cols-2 gap-8">
                            {patientQuestionnaires.map(item => (
                                <Card key={item.id} className={`border-0 shadow-soft rounded-[2.5rem] bg-white border transition-all group overflow-hidden ${item.id === lastCreatedId ? 'ring-2 ring-brand-500 border-transparent' : 'border-slate-50'}`}>
                                    <div className="p-8 h-full flex flex-col">
                                        <div className="mb-6 flex justify-between items-start">
                                            <Badge className="bg-slate-50 text-slate-400 border-slate-100 px-3 py-1 text-[10px] font-black uppercase tracking-widest rounded-xl">Portal Release</Badge>
                                             {item.id === lastCreatedId && <Badge className="bg-brand-600 text-white border-0 shadow-sm px-3 py-1 text-[10px] font-black uppercase tracking-widest rounded-xl">New</Badge>}
                                        </div>
                                        <h3 className="text-xl font-black text-slate-900 group-hover:text-brand-600 transition-colors uppercase tracking-tight font-outfit mb-3">{item.title}</h3>
                                        <div className="flex items-center gap-4 text-[10px] font-black uppercase text-slate-400 tracking-widest mb-10">
                                            <span className="flex items-center gap-1.5 font-black">{item.questions.length} Questions</span>
                                            <span className="flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" /> {item.duration}</span>
                                        </div>
                                        <Button 
                                            variant="outline" 
                                            className="w-full h-14 rounded-2xl border-slate-200 text-slate-600 hover:bg-slate-50 font-black text-xs uppercase tracking-[0.2em] transition-all"
                                            onClick={() => openAssignModal(item.id)}
                                        >
                                            Assign to Patient <ChevronRight className="ml-3 h-4 w-4" />
                                        </Button>
                                    </div>
                                </Card>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Right Column: History */}
                <div className="xl:col-span-4 space-y-10">
                    <div className="flex items-center gap-4 mb-4">
                         <div className="h-10 w-10 rounded-xl bg-slate-900 flex items-center justify-center text-brand-400 shadow-sm">
                            <History className="h-5 w-5" />
                         </div>
                         <h2 className="text-xl font-black text-slate-900 font-outfit uppercase tracking-tight">Audit Trail</h2>
                    </div>

                    <Card className="border-0 shadow-soft rounded-[2.5rem] overflow-hidden bg-white border border-slate-50">
                        <CardContent className="p-0">
                            <div className="divide-y divide-slate-50">
                                {RECENT_HISTORY.slice(0, 10).map((hist, idx) => (
                                    <div key={idx} className="p-6 hover:bg-slate-50/50 transition-all group flex flex-col gap-4">
                                        <div className="flex justify-between items-center">
                                            <Badge className={`rounded-lg px-2 py-0.5 text-[8px] font-black uppercase tracking-tighter ${hist.submitterRole === 'Patient' ? 'bg-teal-50 text-teal-600' : 'bg-brand-50 text-brand-600'}`}>
                                                Origin: {hist.submitterRole || 'Clinical'}
                                            </Badge>
                                            <span className="text-[10px] font-bold text-slate-300">{hist.date}</span>
                                        </div>
                                        <div>
                                            <p className="font-black text-slate-900 text-sm tracking-tight font-outfit uppercase">{hist.student}</p>
                                            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1">{hist.assessment}</p>
                                        </div>
                                        <div className="flex items-center justify-between mt-2">
                                            <div className="flex items-center gap-2">
                                                <div className={`h-2 w-2 rounded-full ${hist.score === 'Pending' ? 'bg-amber-400' : 'bg-green-500'}`}></div>
                                                <span className="text-[10px] font-black text-slate-600 uppercase tracking-widest">{hist.score}</span>
                                            </div>
                                            {(hist.submitterRole === 'Patient' && hist.id && hist.id !== 'h1') && (
                                                <Button 
                                                    variant="ghost" 
                                                    size="sm" 
                                                    className="h-8 rounded-xl px-3 text-[10px] font-black uppercase tracking-widest text-brand-600 hover:bg-brand-50" 
                                                    onClick={() => navigate(`/app/assessments/analysis/${hist.id}`)}
                                                >
                                                    Analysis <BarChart2 className="ml-1.5 h-3.5 w-3.5" />
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </div>

            {/* Assign Modal */}
            {isAssignModalOpen && (
                <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xl z-[100] flex items-center justify-center p-6">
                    <Card className="w-full max-w-xl shadow-premium border-0 rounded-[3rem] overflow-hidden bg-white">
                        <div className="p-10 border-b border-slate-50 flex justify-between items-center bg-slate-50/50">
                            <div>
                                <h2 className="text-3xl font-black text-slate-900 font-outfit tracking-tight uppercase">Dispense Form</h2>
                                <p className="text-slate-400 font-bold text-[10px] uppercase tracking-widest mt-1">Release secure evaluation protocol to portal</p>
                            </div>
                            <Button variant="ghost" size="icon" onClick={() => setAssignModalOpen(false)} className="h-10 w-10 bg-white shadow-sm border border-slate-100 rounded-xl hover:text-red-500 transition-colors"><X className="h-5 w-5" /></Button>
                        </div>

                        {showSuccess ? (
                            <div className="p-20 text-center">
                                <div className="mx-auto w-24 h-24 bg-brand-50 text-brand-600 rounded-[2.5rem] flex items-center justify-center mb-10 shadow-inner">
                                    <CheckCircle className="h-12 w-12" />
                                </div>
                                <h3 className="text-3xl font-black text-slate-900 font-outfit uppercase tracking-tight">Form Dispensed</h3>
                                <p className="text-slate-400 font-medium mt-4">The clinical portal has updated the patient view.</p>
                            </div>
                        ) : (
                            <div className="p-10 space-y-10">
                                <div className="space-y-4">
                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] block px-1">1. Clinical Profile</label>
                                    <select
                                        className="w-full h-16 rounded-[1.5rem] bg-slate-50 border-none focus:ring-2 focus:ring-brand-500 font-bold text-slate-900 px-6 transition-all"
                                        value={selectedPatientId}
                                        onChange={(e) => setSelectedPatientId(e.target.value)}
                                    >
                                        <option value="" disabled>Select active profile...</option>
                                        {profiles.map(p => (
                                            <option key={p.id} value={p.id}>{p.name}</option>
                                        ))}
                                    </select>
                                </div>

                                <div className="space-y-4">
                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] block px-1">2. Target Architecture</label>
                                    <select
                                        className="w-full h-16 rounded-[1.5rem] bg-slate-50 border-none focus:ring-2 focus:ring-brand-500 font-bold text-slate-900 px-6 transition-all"
                                        value={selectedQuestionnaireId}
                                        onChange={(e) => setSelectedQuestionnaireId(e.target.value)}
                                    >
                                        <option value="" disabled>Choose protocol...</option>
                                        {patientQuestionnaires.map(q => (
                                            <option key={q.id} value={q.id}>{q.title}</option>
                                        ))}
                                    </select>
                                </div>

                                <div className="space-y-4">
                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] block px-1">3. Clinical Frequency</label>
                                    <select
                                        className="w-full h-16 rounded-[1.5rem] bg-slate-50 border-none focus:ring-2 focus:ring-brand-500 font-bold text-slate-900 px-6 transition-all"
                                        value={selectedFrequency}
                                        onChange={(e) => setSelectedFrequency(e.target.value)}
                                    >
                                        {['Daily', 'Weekly', 'Monthly', 'Annually'].map(f => (
                                            <option key={f} value={f}>{f}</option>
                                        ))}
                                    </select>
                                </div>

                                <Button 
                                    className="w-full h-16 rounded-[1.5rem] bg-brand-600 hover:bg-brand-700 text-white font-black text-sm uppercase tracking-widest shadow-premium transition-all mt-4" 
                                    onClick={handleAssignToPortal}
                                >
                                    Confirm Dispatch <Send className="ml-3 h-4 w-4" />
                                </Button>
                            </div>
                        )}
                    </Card>
                </div>
            )}

            {/* Create Template Modal */}
            {isCreateModalOpen && (
                <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xl z-[100] flex items-center justify-center p-6">
                    <Card className="w-full max-w-2xl shadow-premium border-0 rounded-[3rem] overflow-hidden bg-white max-h-[90vh] flex flex-col">
                        <div className="p-10 border-b border-slate-50 flex justify-between items-center bg-slate-50/50 flex-shrink-0">
                            <div>
                                <h2 className="text-3xl font-black text-slate-900 font-outfit tracking-tight uppercase">Template Builder</h2>
                                <p className="text-slate-400 font-bold text-[10px] uppercase tracking-widest mt-1">Design a new clinical protocol for portal release</p>
                            </div>
                            <Button variant="ghost" size="icon" onClick={() => setCreateModalOpen(false)} className="h-10 w-10 bg-white shadow-sm border border-slate-100 rounded-xl hover:text-red-500 transition-colors"><X className="h-5 w-5" /></Button>
                        </div>

                        {createSuccess ? (
                            <div className="p-20 text-center">
                                <div className="mx-auto w-24 h-24 bg-brand-50 text-brand-600 rounded-[2.5rem] flex items-center justify-center mb-10 shadow-inner">
                                    <CheckCircle className="h-12 w-12" />
                                </div>
                                <h3 className="text-3xl font-black text-slate-900 font-outfit uppercase tracking-tight">Template Created</h3>
                                <p className="text-slate-400 font-medium mt-4">The new protocol is now available in your clinical library.</p>
                            </div>
                        ) : (
                            <div className="flex-1 overflow-y-auto p-10 space-y-10 custom-scrollbar focus:outline-none">
                                <div className="grid grid-cols-2 gap-8">
                                    <div className="space-y-4">
                                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block px-1">Protocol Title</label>
                                        <Input 
                                            placeholder="e.g. Daily Progress Log" 
                                            value={createTitle}
                                            onChange={(e) => setCreateTitle(e.target.value)}
                                            className="h-16 rounded-[1.5rem] bg-slate-50 border-none font-bold"
                                        />
                                    </div>
                                    <div className="space-y-4">
                                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block px-1">Estimated Time</label>
                                        <Input 
                                            placeholder="~10 mins" 
                                            value={createDuration}
                                            onChange={(e) => setCreateDuration(e.target.value)}
                                            className="h-16 rounded-[1.5rem] bg-slate-50 border-none font-bold"
                                        />
                                    </div>
                                </div>

                                <div className="space-y-6">
                                    <div className="flex items-center justify-between">
                                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block px-1 font-bold">Protocol Items (Questions)</label>
                                        <Button variant="outline" size="sm" onClick={handleAddQuestion} className="rounded-xl h-10 px-4 border-slate-200 font-black text-[9px] uppercase tracking-widest text-brand-600 hover:bg-brand-50">
                                            <Plus className="mr-2 h-3 w-3" /> Add Item
                                        </Button>
                                    </div>
                                    <div className="space-y-4">
                                        {createQuestions.map((q, idx) => (
                                            <div key={idx} className="flex gap-3 animate-in fade-in slide-in-from-top-2 duration-300">
                                                <div className="flex-1">
                                                    <Input 
                                                        placeholder={`Item #${idx + 1}`}
                                                        value={q}
                                                        onChange={(e) => handleQuestionChange(idx, e.target.value)}
                                                        className="h-14 rounded-2xl bg-slate-50 border-none font-medium text-slate-900"
                                                    />
                                                </div>
                                                {createQuestions.length > 1 && (
                                                    <Button variant="ghost" size="icon" onClick={() => handleRemoveQuestion(idx)} className="h-14 w-14 rounded-2xl bg-red-50 text-red-400 hover:text-red-500 hover:bg-red-100 transition-colors">
                                                        <Trash2 className="h-4 w-4" />
                                                    </Button>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                
                                <Button 
                                    className="w-full h-16 rounded-2xl bg-brand-600 hover:bg-brand-700 text-white font-black text-sm uppercase tracking-widest shadow-premium transition-all mt-4" 
                                    onClick={handleCreateSubmit}
                                >
                                    Finalize Template <CheckCircle className="ml-3 h-4 w-4" />
                                </Button>
                            </div>
                        )}
                    </Card>
                </div>
            )}
            {/* Clinical Patient Selection Modal */}
            {isPatientModalOpen && (
                <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xl z-[100] flex items-center justify-center p-6">
                    <Card className="w-full max-w-xl shadow-premium border-0 rounded-[3rem] overflow-hidden bg-white">
                        <div className="p-10 border-b border-slate-50 flex justify-between items-center bg-slate-50/50">
                            <div>
                                <h2 className="text-3xl font-black text-slate-900 font-outfit tracking-tight uppercase">Select Patient</h2>
                                <p className="text-slate-400 font-bold text-[10px] uppercase tracking-widest mt-1">Initialize {selectedClinicalAssessment?.title}</p>
                            </div>
                            <Button variant="ghost" size="icon" onClick={() => setPatientModalOpen(false)} className="h-10 w-10 bg-white shadow-sm border border-slate-100 rounded-xl hover:text-red-500 transition-colors"><X className="h-5 w-5" /></Button>
                        </div>
                        
                        <div className="p-10 space-y-4 max-h-[60vh] overflow-y-auto custom-scrollbar">
                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest px-1 mb-2">Assigned Clinical Profiles</p>
                            {assignedProfiles.length > 0 ? assignedProfiles.map(p => (
                                <button 
                                    key={p.id}
                                    onClick={() => confirmClinicalStart(p)}
                                    className="w-full flex items-center justify-between p-6 rounded-2xl bg-slate-50 border border-slate-100 hover:border-brand-500 hover:bg-brand-50 transition-all group"
                                >
                                    <div className="text-left">
                                        <p className="font-black text-slate-900 group-hover:text-brand-600 uppercase font-outfit">{p.name}</p>
                                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-tight">{p.diagnosis} • {p.age}y</p>
                                    </div>
                                    <ChevronRight className="h-5 w-5 text-slate-300 group-hover:text-brand-600" />
                                </button>
                            )) : (
                                <div className="text-center py-10 opacity-50">
                                    <p className="font-bold text-slate-400">No patients assigned to your care.</p>
                                </div>
                            )}
                        </div>
                    </Card>
                </div>
            )}
        </div>
    );
}
